import { Link, styled, useTranslation } from '@obvio/app'

import { blogPagePath } from './consts'

import type { ReactElement } from 'react'

const Nav = styled.nav`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${(theme) => theme.spacing.small};
  margin: ${(theme) => theme.spacing.large} 0;
`

const PageLink = styled(Link)<{ $current: boolean }>`
  min-width: 2.5rem;
  padding: 0.5rem 0.75rem;
  text-align: center;
  border: 1px solid ${(theme) => theme.stroke};
  font-weight: ${(_, { $current }) => ($current ? 500 : 300)};
`

type BlogPaginationProps = {
  /** Number of archive pages. */
  total: number
  /** Archive page being displayed; omitted on the blog index. */
  current?: number | undefined
}

/**
 * Plain `<a href>` links to every archive page, so crawlers (which do not run
 * JavaScript tabs) can reach every post.
 */
export function BlogPagination({
  total,
  current,
}: BlogPaginationProps): ReactElement | null {
  const { t } = useTranslation()

  if (total < 2) {
    return null
  }

  const pages = Array.from({ length: total }, (_, index) => index + 1)

  return (
    <Nav aria-label={t('blog.pagination')}>
      {current && current > 1 ? (
        <PageLink href={blogPagePath(current - 1)} $current={false} rel="prev">
          {t('blog.previous')}
        </PageLink>
      ) : null}
      {pages.map((page) => (
        <PageLink
          key={page}
          href={blogPagePath(page)}
          $current={page === current}
          aria-current={page === current ? 'page' : undefined}
        >
          {page}
        </PageLink>
      ))}
      {current && current < total ? (
        <PageLink href={blogPagePath(current + 1)} $current={false} rel="next">
          {t('blog.next')}
        </PageLink>
      ) : null}
    </Nav>
  )
}
